import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';

export const ARCHIVE_DOWNLOAD_PREVIEW_QUERY_KEY = 'archive-download-preview';

export interface ArchiveDownloadPreviewInput {
  folderIds?: string[];
  assetIds?: string[];
  /** Skip the request (e.g. modal is closed). */
  enabled?: boolean;
}

export interface ArchiveDownloadPreviewState {
  fileCount?: number;
  subfolderCount?: number;
  maxFiles?: number;
  maxBytes?: number;
  isLoading: boolean;
  error?: string;
}

export const useArchiveDownloadPreview = ({
  folderIds,
  assetIds,
  enabled = true,
}: ArchiveDownloadPreviewInput): ArchiveDownloadPreviewState => {
  const { getTenantId } = useTenants();
  const hasSelection = (folderIds?.length ?? 0) + (assetIds?.length ?? 0) > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: [ARCHIVE_DOWNLOAD_PREVIEW_QUERY_KEY, getTenantId(), folderIds ?? [], assetIds ?? []],
    queryFn: async () => {
      const response = await api.archiveDownloads.previewArchiveDownload(getTenantId(), {
        folderIds,
        assetIds,
      });
      return response.data;
    },
    enabled: enabled && hasSelection,
  });

  return {
    fileCount: data?.fileCount,
    subfolderCount: data?.subfolderCount,
    maxFiles: data?.maxFiles,
    maxBytes: data?.maxBytes,
    isLoading,
    error: getApiError(error, 'Unable to preview archive')?.message,
  };
};
